import type {
    AgentInteractionState,
    OrchestratorAgent,
    OrchestratorState,
    SimpleAgentConfig
} from '@demo-portal/shared';
import React, { useEffect, useState } from 'react';

interface OrchestrationFlowVisualizerProps {
  orchestrator: OrchestratorAgent;
  agents: SimpleAgentConfig;
  orchestratorState: OrchestratorState;
  agentInteractions: Map<string, AgentInteractionState>;
  primaryColor?: string;
}

interface FlowNode {
  id: string;
  type: 'orchestrator' | 'agent' | 'step';
  name: string;
  avatar: string;
  status: 'idle' | 'active' | 'processing' | 'complete' | 'waiting';
  position: { x: number; y: number };
  progress?: number;
  currentAction?: string;
}

interface FlowConnection {
  from: string;
  to: string;
  active: boolean;
  type: 'coordination' | 'data' | 'response';
  message?: string;
}

const OrchestrationFlowVisualizer: React.FC<OrchestrationFlowVisualizerProps> = ({
  orchestrator,
  agents,
  orchestratorState,
  agentInteractions,
  primaryColor = '#667eea',
}) => {
  const [flowNodes, setFlowNodes] = useState<FlowNode[]>([]);
  const [connections, setConnections] = useState<FlowConnection[]>([]);
  const [animatingConnections, setAnimatingConnections] = useState<Set<string>>(new Set());

  // Update flow visualization based on orchestration state
  useEffect(() => {
    const nodes: FlowNode[] = [];
    const newConnections: FlowConnection[] = [];

    // Add orchestrator node (center)
    nodes.push({
      id: 'orchestrator',
      type: 'orchestrator',
      name: orchestrator.name,
      avatar: orchestrator.avatar,
      status: orchestratorState.status === 'processing' || orchestratorState.status === 'synthesizing' 
        ? 'processing' 
        : orchestratorState.status === 'ready' 
        ? 'active' 
        : 'idle',
      position: { x: 50, y: 30 },
    });

    // Add agent nodes in a circular pattern
    agents.forEach((agent, index) => {
      const angle = (index * 2 * Math.PI) / agents.length;
      const radius = 35;
      const centerX = 50;
      const centerY = 65;

      const interaction = agentInteractions.get(agent.id);
      let status: FlowNode['status'] = 'idle';
      
      if (interaction) {
        switch (interaction.status) {
          case 'called':
            status = 'active';
            break;
          case 'processing':
          case 'responding':
            status = 'processing';
            break;
          case 'complete':
            status = 'complete';
            break;
          default:
            status = 'idle';
        }
      }

      nodes.push({
        id: agent.id,
        type: 'agent',
        name: agent.name,
        avatar: agent.avatar,
        status,
        position: {
          x: centerX + radius * Math.cos(angle - Math.PI / 2),
          y: centerY + radius * Math.sin(angle - Math.PI / 2),
        },
        progress: interaction?.progress || 0,
        currentAction: interaction?.currentAction || undefined,
      });

      // Add connections based on current orchestration state
      if (orchestratorState.currentStep) {
        const currentStep = orchestratorState.currentStep;
        
        if (currentStep.action === 'call_agent' && currentStep.agent === agent.id) {
          newConnections.push({
            from: 'orchestrator',
            to: agent.id,
            active: true,
            type: 'coordination',
            message: `Delegating: ${currentStep.description}`,
          });
        } else if (currentStep.action === 'synthesize' && interaction?.status === 'complete') {
          newConnections.push({
            from: agent.id,
            to: 'orchestrator',
            active: true,
            type: 'response',
            message: 'Sending results',
          });
        }
      }
    });

    // Add processing queue visualization
    if (orchestratorState.processingQueue.length > 0) {
      orchestratorState.processingQueue.forEach((step, index) => {
        const agent = agents.find(a => a.id === step.agent);
        if (agent) {
          newConnections.push({
            from: 'orchestrator',
            to: step.agent,
            active: false,
            type: 'coordination',
            message: `Queued: ${step.description}`,
          });
        }
      });
    }

    setFlowNodes(nodes);
    setConnections(newConnections);

    // Animate active connections
    const activeConnectionKeys = newConnections
      .filter(conn => conn.active)
      .map(conn => `${conn.from}-${conn.to}`);
    
    setAnimatingConnections(new Set(activeConnectionKeys));

    // Remove animation after delay
    const timer = setTimeout(() => {
      setAnimatingConnections(new Set());
    }, 1500);

    return () => clearTimeout(timer);
  }, [orchestrator, agents, orchestratorState, agentInteractions]);

  const getStatusColor = (status: FlowNode['status']) => {
    switch (status) {
      case 'active':
        return '#f39c12';
      case 'processing':
        return primaryColor;
      case 'complete':
        return '#27ae60';
      case 'waiting':
        return '#95a5a6';
      default:
        return '#ecf0f1';
    }
  };

  const getConnectionColor = (type: FlowConnection['type'], active: boolean) => {
    if (!active) return '#bdc3c7';
    
    switch (type) {
      case 'coordination':
        return '#e74c3c';
      case 'data':
        return '#3498db';
      case 'response':
        return '#27ae60';
      default:
        return primaryColor;
    }
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '15px',
      padding: '20px',
      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
      height: '500px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        fontSize: '1.3em',
        fontWeight: 'bold',
        marginBottom: '20px',
        color: '#2c3e50',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
      }}>
        <span style={{ fontSize: '1.2em' }}>🎯</span>
        Orchestration Flow
        <div style={{
          fontSize: '0.7em',
          background: getStatusColor(
            orchestratorState.status === 'processing' || orchestratorState.status === 'synthesizing' 
              ? 'processing' 
              : orchestratorState.status === 'ready' 
              ? 'active' 
              : 'idle'
          ),
          color: 'white',
          padding: '4px 8px',
          borderRadius: '12px',
          textTransform: 'uppercase',
          fontWeight: 'bold',
          marginLeft: '10px',
        }}>
          {orchestratorState.status}
        </div>
      </div>

      {/* SVG for connections */}
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        <defs>
          {/* Arrow markers for different connection types */}
          <marker
            id="coordination-arrow"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill="#e74c3c"
            />
          </marker>
          <marker
            id="data-arrow"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill="#3498db"
            />
          </marker>
          <marker
            id="response-arrow"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill="#27ae60"
            />
          </marker>

          {/* Animated gradient for active connections */}
          <linearGradient id="activeConnectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={primaryColor} stopOpacity="0.3">
              <animate
                attributeName="stop-opacity"
                values="0.3;0.9;0.3"
                dur="2s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="50%" stopColor={primaryColor} stopOpacity="0.9">
              <animate
                attributeName="stop-opacity"
                values="0.9;1;0.9"
                dur="2s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="100%" stopColor={primaryColor} stopOpacity="0.3">
              <animate
                attributeName="stop-opacity"
                values="0.3;0.9;0.3"
                dur="2s"
                repeatCount="indefinite"
              />
            </stop>
          </linearGradient>
        </defs>

        {/* Render connections */}
        {connections.map((connection, index) => {
          const fromNode = flowNodes.find(n => n.id === connection.from);
          const toNode = flowNodes.find(n => n.id === connection.to);
          
          if (!fromNode || !toNode) return null;

          const connectionKey = `${connection.from}-${connection.to}`;
          const isAnimating = animatingConnections.has(connectionKey);
          const connectionColor = getConnectionColor(connection.type, connection.active);

          return (
            <g key={index}>
              <line
                x1={`${fromNode.position.x}%`}
                y1={`${fromNode.position.y}%`}
                x2={`${toNode.position.x}%`}
                y2={`${toNode.position.y}%`}
                stroke={isAnimating ? 'url(#activeConnectionGradient)' : connectionColor}
                strokeWidth={connection.active ? "4" : "2"}
                strokeDasharray={connection.active ? (isAnimating ? "8,4" : "none") : "5,5"}
                markerEnd={`url(#${connection.type}-arrow)`}
                opacity={connection.active ? 1 : 0.4}
              >
                {isAnimating && (
                  <animate
                    attributeName="stroke-dashoffset"
                    values="0;-12"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                )}
              </line>
            </g>
          );
        })}
      </svg>

      {/* Flow nodes */}
      {flowNodes.map(node => (
        <div
          key={node.id}
          style={{
            position: 'absolute',
            left: `${node.position.x}%`,
            top: `${node.position.y}%`,
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
            transition: 'all 0.3s ease',
          }}
        >
          {/* Node circle */}
          <div style={{
            width: node.type === 'orchestrator' ? '80px' : '70px',
            height: node.type === 'orchestrator' ? '80px' : '70px',
            borderRadius: '50%',
            background: node.type === 'orchestrator'
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : `linear-gradient(135deg, ${getStatusColor(node.status)}, ${getStatusColor(node.status)}dd)`,
            border: `3px solid ${getStatusColor(node.status)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: node.type === 'orchestrator' ? '2em' : '1.8em',
            boxShadow: node.status === 'processing' 
              ? `0 0 20px ${getStatusColor(node.status)}66` 
              : '0 4px 15px rgba(0,0,0,0.2)',
            animation: node.status === 'processing' ? 'pulse 2s infinite' : 'none',
            cursor: 'pointer',
            position: 'relative',
          }}
          title={`${node.name} - ${node.status}`}
        >
          {node.avatar}
          
          {/* Progress ring for processing agents */}
          {node.status === 'processing' && node.progress !== undefined && (
            <svg
              style={{
                position: 'absolute',
                top: '-5px',
                left: '-5px',
                width: node.type === 'orchestrator' ? '90px' : '80px',
                height: node.type === 'orchestrator' ? '90px' : '80px',
                transform: 'rotate(-90deg)',
              }}
            >
              <circle
                cx="50%"
                cy="50%"
                r={node.type === 'orchestrator' ? '40' : '35'}
                fill="none"
                stroke="rgba(255,255,255,0.3)"
                strokeWidth="3"
              />
              <circle
                cx="50%"
                cy="50%"
                r={node.type === 'orchestrator' ? '40' : '35'}
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeDasharray={`${2 * Math.PI * (node.type === 'orchestrator' ? 40 : 35)}`}
                strokeDashoffset={`${2 * Math.PI * (node.type === 'orchestrator' ? 40 : 35) * (1 - node.progress / 100)}`}
                style={{ transition: 'stroke-dashoffset 0.3s ease' }}
              />
            </svg>
          )}
        </div>
        
        {/* Node label */}
        <div style={{
          position: 'absolute',
          top: node.type === 'orchestrator' ? '90px' : '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '0.75em',
          fontWeight: 'bold',
          color: node.type === 'orchestrator' ? '#667eea' : '#2c3e50',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          maxWidth: '100px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {node.name.split(' ')[0]}
          {node.type === 'orchestrator' && (
            <div style={{
              fontSize: '0.8em',
              color: '#7f8c8d',
              fontWeight: 'normal',
              marginTop: '2px',
            }}>
              Coordinator
            </div>
          )}
        </div>

        {/* Current action indicator */}
        {node.currentAction && node.status === 'processing' && (
          <div style={{
            position: 'absolute',
            top: '-25px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '0.6em',
            whiteSpace: 'nowrap',
            maxWidth: '120px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            zIndex: 3,
          }}>
            {node.currentAction}
          </div>
        )}
      </div>
      ))}

      {/* Status panel */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        right: '20px',
        background: 'rgba(255,255,255,0.95)',
        border: `2px solid ${primaryColor}`,
        borderRadius: '12px',
        padding: '15px',
        fontSize: '0.85em',
        color: '#2c3e50',
        zIndex: 3,
        backdropFilter: 'blur(10px)',
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '8px' 
        }}>
          <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📊</span>
            Orchestration Status
          </div>
          <div style={{
            display: 'flex',
            gap: '12px',
            fontSize: '0.8em',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#27ae60' }} />
              Complete: {Array.from(agentInteractions.values()).filter(a => a.status === 'complete').length}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: primaryColor }} />
              Processing: {Array.from(agentInteractions.values()).filter(a => a.status === 'processing' || a.status === 'responding').length}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#95a5a6' }} />
              Queued: {orchestratorState.processingQueue.length}
            </div>
          </div>
        </div>
        
        {orchestratorState.currentStep && (
          <div style={{ 
            background: 'rgba(102, 126, 234, 0.1)',
            padding: '8px 12px',
            borderRadius: '8px',
            borderLeft: `4px solid ${primaryColor}`,
          }}>
            <strong>Current Step:</strong> {orchestratorState.currentStep.description}
          </div>
        )}
        
        {connections.filter(c => c.active && c.message).length > 0 && (
          <div style={{ marginTop: '8px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Active Communications:</div>
            {connections.filter(c => c.active && c.message).map((conn, index) => (
              <div key={index} style={{ 
                fontSize: '0.9em', 
                color: '#666',
                marginLeft: '8px',
                marginBottom: '2px',
              }}>
                • {conn.message}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CSS animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: translate(-50%, -50%) scale(1); }
            50% { transform: translate(-50%, -50%) scale(1.08); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default OrchestrationFlowVisualizer;